/* eslint-disable react-native/no-color-literals */
import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  TouchableWithoutFeedback,
  FlatList,
  StyleSheet,
  ScrollView,
  Dimensions,
} from 'react-native';

import RowView from './RowView';
import BillingInfoLabel from './BillingInfoLabel';
import BaseText from './BaseText';
import { CheckboxInner } from './Checkbox';

import { CommonProperties, Color } from '../styles';
import { scale } from '../utils/scale.utils';

const { height: screenHeight, width: screenWidth } = Dimensions.get('window');

const ModalDropdownOption = ({ onPress, item, disabledLabel, selectedItem }) => {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={[styles.option, item.disabled ? styles.disabledOption : null]}
    >
      <RowView style={styles.item}>
        <BaseText style={item.disabled && styles.disabledLabel}>
          {'\t'}
          {item.label}
          {item.disabled && disabledLabel && ` - ${disabledLabel}`}
        </BaseText>
        {selectedItem && selectedItem.value === item.value && <CheckboxInner selected={true} />}
      </RowView>
    </TouchableOpacity>
  );
};

const ModalDropdown = ({
  onValueChange,
  value,
  items,
  placeholder,
  modalTitle,
  modalIcon,
  disabledLabel,
  pinneditem = null,
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const handleSelect = (item) => {
    if (!item.disabled) {
      setSelectedItem(item);
      setModalVisible(false);
      onValueChange(item.value);
    }
  };

  useEffect(() => {
    if (!value) {
      return;
    }
    const foundItem = items?.find((item) => {
      return item.value === value;
    });
    if (foundItem) {
      setSelectedItem(foundItem);
      return;
    }
    if (pinneditem && value === pinneditem.value) {
      setSelectedItem(pinneditem);
      return;
    }
  }, [items, pinneditem, value]);

  return (
    <View>
      <TouchableOpacity style={styles.dropdownHeader} onPress={() => setModalVisible(true)}>
        <Text style={styles.dropdownHeaderText}>
          {selectedItem ? selectedItem.label : placeholder.label || 'Select an option...'}
        </Text>
        <View style={styles.caretContainer}>
          <View style={styles.caret}></View>
          <View style={styles.overCaret}></View>
        </View>
      </TouchableOpacity>

      <Modal
        transparent={true}
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        <TouchableWithoutFeedback onPress={() => setModalVisible(false)}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalContainer}>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <BillingInfoLabel text={modalTitle} icon={modalIcon} fromPickerModal={true} />
                </View>
                {/* Horizontal Line */}
                <View style={styles.separator} />
                <ScrollView style={styles.optionContainer}>
                  <FlatList
                    data={items}
                    keyExtractor={(item) => item.value}
                    renderItem={({ item }) => (
                      <ModalDropdownOption
                        onPress={() => {
                          handleSelect(item);
                        }}
                        item={item}
                        disabledLabel={disabledLabel}
                        selectedItem={selectedItem}
                      />
                    )}
                  />
                </ScrollView>
                {pinneditem && (
                  <View style={styles.pinnedOptionContainer}>
                    <ModalDropdownOption
                      onPress={() => {
                        handleSelect(pinneditem);
                      }}
                      item={pinneditem}
                      disabledLabel={disabledLabel}
                      selectedItem={selectedItem}
                    />
                  </View>
                )}
              </View>
            </View>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: screenWidth * 0.9,
    maxHeight: screenHeight * 0.8, // Adjust max height as needed
    backgroundColor: Color.accent,
    borderRadius: 4,
  },
  modalContent: {
    padding: 16,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  separator: {
    height: 1,
    backgroundColor: Color.text.low,
    marginVertical: 1,
  },
  optionContainer: {
    maxHeight: screenHeight * 0.5, // Max height for options list, adjust as needed
  },
  option: {
    paddingHorizontal: scale(2),
    padding: scale(5),
    marginVertical: scale(2),
  },
  item: {
    ...CommonProperties.px2,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dropdownHeader: {
    padding: scale(8),
    paddingRight: scale(1),
    borderWidth: 1,
    borderRadius: 4,
    backgroundColor: Color.accent,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flex: 1,
    color: Color.text.high,
    ...CommonProperties.fontFamily,
    ...CommonProperties.body,
    ...CommonProperties.borderRadius,
    ...CommonProperties.my2,
    ...CommonProperties.px2,
    marginBottom: scale(18),
  },
  dropdownHeaderText: {
    fontSize: 12,
    paddingLeft: 5,
    ...CommonProperties.fontFamily,
    ...CommonProperties.body,
  },
  caretContainer: {
    width: 20, // Adjust the width as needed
    height: 20, // Adjust the height as needed
    alignItems: 'center',
    justifyContent: 'center',
  },
  caret: {
    position: 'absolute',
    width: 0,
    height: 0,
    borderLeftWidth: 5,
    borderLeftColor: Color.transparent,
    borderRightWidth: 5,
    borderRightColor: Color.transparent,
    borderBottomWidth: 5,
    borderBottomColor: Color.black,
    transform: [{ rotate: '180deg' }], // Rotate to make it point downwards
  },
  overCaret: {
    position: 'absolute',
    top: 5, // Adjust as needed to overlap correctly
    width: 0,
    height: 0,
    borderLeftWidth: 5,
    borderLeftColor: Color.transparent,
    borderRightWidth: 5,
    borderRightColor: Color.transparent,
    borderBottomWidth: 5,
    borderBottomColor: Color.accent, // Overlay to hide part of the caret
    transform: [{ rotate: '180deg' }], // Rotate to make it point downwards
  },
  disabledOption: {
    backgroundColor: Color.addReview.background,
  },
  disabledLabel: {
    color: Color.gray,
  },
  pinnedOptionContainer: { marginVertical: 2 },
});

export default ModalDropdown;
